import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ConfirmationModal = ({ isOpen, onClose, confirmationNumber, booking, guestInfo }) => {
  if (!isOpen) return null;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        {/* Success Header */}
        <div className="bg-gradient-to-r from-success to-success/80 p-8 text-center">
          <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle" size={48} className="text-white" />
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Booking Confirmed!</h2>
          <p className="text-white/90">Your reservation has been successfully processed</p>
        </div>

        {/* Confirmation Details */}
        <div className="p-8 space-y-6">
          {/* Confirmation Number */}
          <div className="text-center p-6 bg-primary/5 rounded-xl border border-primary/20">
            <p className="text-sm text-muted-foreground mb-2">Confirmation Number</p>
            <p className="text-3xl font-bold text-primary mb-3">{confirmationNumber}</p>
            <p className="text-xs text-muted-foreground">
              Please save this number for your records
            </p>
          </div>

          {/* Booking Summary */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Icon name="ClipboardList" size={20} className="text-primary" />
              Booking Summary
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Property</span>
                <span className="text-sm font-semibold text-foreground text-right">{booking?.name}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Location</span>
                <span className="text-sm font-semibold text-foreground text-right">{booking?.location}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Check-in</span>
                <span className="text-sm font-semibold text-foreground">{formatDate(booking?.checkIn)}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Check-out</span>
                <span className="text-sm font-semibold text-foreground">{formatDate(booking?.checkOut)}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Guests</span>
                <span className="text-sm font-semibold text-foreground">{booking?.guests} {booking?.guests === 1 ? 'Guest' : 'Guests'}</span>
              </div>
              <div className="pt-3 border-t border-border flex justify-between items-start">
                <span className="text-base font-semibold text-foreground">Total Amount</span>
                <span className="text-xl font-bold text-primary">
                  ₱{booking?.totalAmount?.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Guest Information */}
          <div className="space-y-4">
            <h3 className="text-lg font-bold text-foreground flex items-center gap-2">
              <Icon name="User" size={20} className="text-primary" />
              Guest Information
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Name</span>
                <span className="text-sm font-semibold text-foreground">{guestInfo?.firstName} {guestInfo?.lastName}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Email</span>
                <span className="text-sm font-semibold text-foreground">{guestInfo?.email}</span>
              </div>
              <div className="flex justify-between items-start">
                <span className="text-sm text-muted-foreground">Phone</span>
                <span className="text-sm font-semibold text-foreground">{guestInfo?.phone}</span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="p-4 bg-muted/50 rounded-xl border border-border">
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="ListChecks" size={16} className="text-primary" />
              Next Steps
            </h4>
            <div className="space-y-2">
              <div className="flex items-start gap-3">
                <Icon name="Mail" size={14} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  A confirmation email has been sent to {guestInfo?.email}
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="FileText" size={14} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  You can view and manage your booking in the Bookings Dashboard
                </p>
              </div>
              <div className="flex items-start gap-3">
                <Icon name="Phone" size={14} className="text-success mt-0.5 flex-shrink-0" />
                <p className="text-xs text-muted-foreground">
                  The property will contact you 24 hours before check-in
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={onClose}
              variant="default"
              fullWidth
              iconName="Calendar"
            >
              View My Bookings
            </Button>
            <Button
              onClick={() => window.print()}
              variant="outline"
              fullWidth
              iconName="Printer"
            >
              Print Confirmation
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmationModal;